"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ArrowRight, ChevronRight, CircleCheck as CheckCircle2, Cpu, Layers, Network, Server, ShieldCheck, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const integrationSteps = [{
  id: "step-1",
  tag: "01",
  title: "Process Interface & Probe Mounting",
  desc: "Sanitary flanges, sight-glass adaptors, and ATEX/hazardous zone probe assemblies."
}, {
  id: "step-2",
  tag: "02",
  title: "Analyzer & Fiber Optical Cabling",
  desc: "Rigidized IP65/NEMA 4X enclosures and precision fiber runs to spectrometer engines."
}, {
  id: "step-3",
  tag: "03",
  title: "DCS / SCADA / PLC Data Link",
  desc: "Bidirectional OPC UA, Modbus TCP/IP, Profinet, and 4-20mA industrial outputs."
}];
const technicalSpecs = [{
  label: "Compliance",
  val: "GAMP 5 & 21 CFR Part 11"
}, {
  label: "Protocols",
  val: "OPC UA · Modbus · Profinet"
}, {
  label: "Deployment",
  val: "Ireland & UK On-site"
}];
export default function PatInstallation() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="patinstallationhero" data-nav="light" className="relative w-full overflow-hidden bg-background py-16 md:py-24 lg:py-28">
      {/* Subtle background grid accent */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(120,119,198,0.08),rgba(255,255,255,0))]" />

      <div className="page-container relative">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8 xl:gap-12">
          {/* Left Column (7 cols) */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="flex flex-col items-start lg:col-span-7">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <Link to="/HomePage" className="transition-colors hover:text-foreground">
                Home
              </Link>
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
              <Link to="/ServicesPage" className="transition-colors hover:text-foreground">
                Services
              </Link>
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
              <span className="font-medium text-foreground" aria-current="page">
                PAT Installation &amp; Integration
              </span>
            </nav>

            <Badge variant="outline" className="mb-6 gap-2 border-border bg-secondary/60 px-3.5 py-1.5 text-xs font-semibold tracking-wide text-secondary-foreground uppercase">
              <Zap className="h-3.5 w-3.5 text-primary" />
              TURNKEY COMMISSIONING &amp; INTEGRATION · IRELAND &amp; UK
            </Badge>

            <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl/tight">
              PAT Installation &amp; System Integration
            </h1>

            <p className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
              End-to-end on-site deployment, optical calibration, and control
              system integration for inline NIR, Raman, and process testing
              hardware. We bridge the gap between analytical instrumentation and
              your plant PLC/DCS architecture with full regulatory compliance.
            </p>

            {/* Dual CTAs */}
            <div className="mt-8 flex w-full flex-col gap-3.5 sm:w-auto sm:flex-row sm:items-center">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="/DiscussYourApplicationPage" className="gap-2">
                  <span>Schedule Site Consultation</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/QualificationLifecycleServicesPage">
                  <span>Explore Scope of Work</span>
                </Link>
              </Button>
            </div>

            {/* Quick Specs / Trust badges */}
            <div className="mt-10 grid w-full grid-cols-1 gap-4 border-t border-border pt-8 sm:grid-cols-3">
              {technicalSpecs.map((spec, i) => <div key={spec.label} data-index={i} className="flex flex-col gap-1">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {spec.label}
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    {spec.val}
                  </span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column: Engineering Schematic Visual Card (5 cols) */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} transition={{
          delay: 0.15
        }} className="w-full lg:col-span-5">
            <Card className="relative overflow-hidden border-border bg-card shadow-lg">
              {/* Card Header with System Status Bar */}
              <CardHeader className="border-b border-border bg-secondary/30 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-500/20" />
                    <span className="text-xs font-semibold tracking-wider text-foreground uppercase">
                      Process Automation Architecture
                    </span>
                  </div>
                  <Badge variant="outline" className="border-border bg-background text-[11px] text-muted-foreground">
                    Rev 3.4
                  </Badge>
                </div>
                <CardTitle className="mt-2 text-lg font-bold text-foreground">
                  PAT System Integration Flow
                </CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  Typical inline spectrometer to plant SCADA/DCS topology
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6 pt-6">
                {/* Schematic Flow Steps */}
                <div className="relative space-y-4">
                  {/* Vertical connector guide */}
                  <div className="absolute top-4 bottom-4 left-4 w-px bg-border" />

                  {integrationSteps.map((step, idx) => <div key={step.id} data-index={idx} className="relative flex items-start gap-3.5 pl-0.5">
                      <div className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-background text-xs font-bold text-primary shadow-xs">
                        {step.tag}
                      </div>
                      <div className="flex-1 rounded-lg border border-border/60 bg-muted/30 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-xs font-semibold text-foreground">
                            {step.title}
                          </h4>
                          {idx === 0 && <Activity className="h-3.5 w-3.5 text-primary" />}
                          {idx === 1 && <Cpu className="h-3.5 w-3.5 text-primary" />}
                          {idx === 2 && <Network className="h-3.5 w-3.5 text-primary" />}
                        </div>
                        <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                          {step.desc}
                        </p>
                      </div>
                    </div>)}
                </div>

                {/* Signal & Controller Block */}
                <div className="rounded-lg border border-border bg-secondary/50 p-3.5">
                  <div className="flex items-center justify-between text-xs font-medium text-foreground">
                    <span className="flex items-center gap-1.5">
                      <Server className="h-3.5 w-3.5 text-muted-foreground" />
                      Host Network Interfaces
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      Real-Time Bus
                    </span>
                  </div>

                  <div className="mt-2.5 grid grid-cols-3 gap-2 text-center text-[10px] font-semibold text-foreground">
                    <div className="rounded-md border border-border bg-background py-1.5">
                      DeltaV DCS
                    </div>
                    <div className="rounded-md border border-border bg-background py-1.5">
                      Siemens PLC
                    </div>
                    <div className="rounded-md border border-border bg-background py-1.5">
                      Rockwell / CIP
                    </div>
                  </div>
                </div>

                {/* Bottom Assurance strip */}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
                  <span>Turnkey SAT, IQ/OQ protocols &amp; factory engineer handoff.</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}